import {
  Badge,
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Select,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import Paginator from '../../../components/paginator'
import { formatDateTime } from '../../../helpers'
import { BlogPostService } from '../../../services/BlogPost/BlogPostService'
import { BlogPost, BlogPostComplete } from '../../../services/BlogPost/types'
import { BlogPostDeleteDialog } from './BlogPostDeleteDialog'
import { BlogPostFormModal } from './BlogPostFormModal'

export default function BlogPostPage() {
  const service = new BlogPostService()
  const ref = useRef<HTMLButtonElement>(null)
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([])
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [targetRecord, setTargetRecord] = useState<BlogPost>()
  const [filters, setFilters] = useState({ search: '', page_size: '10' })
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [recordEditing, setRecordEditing] = useState<BlogPostComplete>()

  useEffect(() => {
    refreshList()
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refreshList()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [filters, page])

  useEffect(() => {
    if (!isCreateModalOpen) {
      refreshList()
    }
  }, [isCreateModalOpen])

  const refreshList = async () => {
    service.list({ ...filters, page }).then((response) => {
      setBlogPosts(response.data)
      setTotalPages(Math.ceil(response.total_filtered / parseInt(filters.page_size)))
    })
  }

  const openEditModal = (id: number) => {
    service.get(id).then((response) => {
      setRecordEditing(response)
      setIsCreateModalOpen(true)
    })
  }

  return (
    <>
      <Heading as="h1" size="lg" marginBottom="5" mb="10" textTransform="uppercase">
        Posts do Blog
      </Heading>

      <Flex mb="4">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            onChange={(e) => setFilters({ ...filters, page_size: e.target.value })}
            value={filters.page_size}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>
        <Spacer />
        <Flex flex="1" justifyContent="flex-end" alignItems="center">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            value={filters.search}
          />
        </Flex>
      </Flex>

      <TableContainer fontSize="smaller" padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th p={1}>ID</Th>
              <Th p={1}>Título</Th>
              <Th p={1}>Cabeçalho</Th>
              <Th p={1}>Popular</Th>
              <Th p={1}>Status</Th>
              <Th p={1}>Criado em</Th>
              <Th p={1}>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {blogPosts.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center">
                  Nenhum post cadastrado
                </Td>
              </Tr>
            ) : (
              blogPosts.map((post) => (
                <Tr key={post.id}>
                  <Td p={1}>{post.id}</Td>
                  <Td p={1} maxW="200px" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    {post.title}
                  </Td>
                  <Td p={1} maxW="200px" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    {post.header}
                  </Td>
                  <Td p={1}>
                    <Badge colorScheme={post.popular ? 'yellow' : 'gray'} fontSize="0.8em" px="2">
                      {post.popular ? 'Popular' : 'Normal'}
                    </Badge>
                  </Td>
                  <Td p={1}>
                    <Badge colorScheme={post.is_active ? 'green' : 'red'} fontSize="0.8em" px="2">
                      {post.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </Td>
                  <Td p={1}>{formatDateTime(post.created_at)}</Td>
                  <Td p={1}>
                    <Tooltip label="Editar" hasArrow placement="top">
                      <IconButton
                        aria-label="Editar"
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          openEditModal(post.id)
                        }}
                      >
                        <i className="lni lni-pencil"></i>
                      </IconButton>
                    </Tooltip>
                    <Tooltip label="Excluir" hasArrow placement="top">
                      <IconButton
                        aria-label="Excluir"
                        colorScheme="red"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setTargetRecord(post)
                          setOpenDeleteDialog(true)
                        }}
                      >
                        <i className="lni lni-trash-can"></i>
                      </IconButton>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
      />

      {targetRecord && (
        <BlogPostDeleteDialog
          isOpen={openDeleteDialog}
          blogPost={targetRecord}
          leastDestructiveRef={ref}
          onClose={() => {
            setOpenDeleteDialog(false)
            refreshList()
          }}
        />
      )}

      <Tooltip label="Adicionar post" aria-label="Adicionar post" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setRecordEditing(undefined)
            setIsCreateModalOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      <BlogPostFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        blogPost={recordEditing}
      />
    </>
  )
}
